/**
 * Utility to decode React minified errors and provide debugging information
 */

// Common React error codes and their meanings
const REACT_ERROR_CODES: Record<string, string> = {
  '301': 'Too many re-renders. React limits the number of renders to prevent an infinite loop.',
  '130': 'Element type is invalid: expected a string (for built-in components) or a class/function (for composite components) but got: %s.',
  '185': 'Element type is invalid: expected a string (for built-in components) or a class/function (for composite components) but got: %s. Did you accidentally export a JSX literal instead of a component?',
  '200': 'Expected the last optional `callback` argument to be a function. Instead received: %s.',
  '31': 'Element type is invalid. Received a promise that resolves to: %s. Lazy element type must resolve to a class or function.',
};

/**
 * Decode a React minified error message
 */
export function decodeReactError(errorMessage: string): {
  originalMessage: string;
  decodedMessage?: string;
  errorCode?: string;
  isReactError: boolean;
  debugUrl?: string;
} {
  // Check if it's a React minified error
  const reactErrorMatch = errorMessage.match(/Minified React error #(\d+)/);
  
  if (!reactErrorMatch) {
    return {
      originalMessage: errorMessage,
      isReactError: false,
    };
  }

  const errorCode = reactErrorMatch[1];
  const decodedMessage = REACT_ERROR_CODES[errorCode];
  const debugUrl = `https://react.dev/errors/${errorCode}`;

  return {
    originalMessage: errorMessage,
    decodedMessage,
    errorCode,
    isReactError: true,
    debugUrl,
  };
}

/**
 * Enhanced error logging for development
 */
export function logEnhancedError(error: Error, componentName?: string): void {
  const decoded = decodeReactError(error.message);
  
  console.group('🚨 Enhanced Error Information');
  console.error('Original Error:', error);
  
  if (componentName) {
    console.log('Component:', componentName);
  }
  
  if (decoded.isReactError) {
    console.log('Error Code:', decoded.errorCode);
    console.log('Decoded Message:', decoded.decodedMessage);
    console.log('Debug URL:', decoded.debugUrl);
    
    if (decoded.errorCode === '301') {
      console.group('💡 Debugging Tips for Error #301:');
      console.log('1. Check for state updates in render method');
      console.log('2. Look for useEffect without proper dependencies');
      console.log('3. Check for event handlers causing immediate re-renders');
      console.log('4. Look for conditional state updates that always trigger');
      console.groupEnd();
    }
  }
  
  console.groupEnd();
}

/**
 * React Error Boundary helper
 */
export class ErrorBoundaryLogger {
  static logError(error: Error, errorInfo: { componentStack?: string }): void {
    const decoded = decodeReactError(error.message);
    
    console.group('🛡️ Error Boundary Caught Error');
    logEnhancedError(error);
    if (errorInfo.componentStack) {
      console.log('Component Stack:', errorInfo.componentStack);
    }
    console.groupEnd();
    
    // In production, you might want to send this to an error tracking service
    if (import.meta.env.PROD) {
      // Example: Send to error tracking service
      // errorTrackingService.captureException(error, { extra: { decoded, errorInfo } });
    }
  }
} 